import { useMemo } from 'react'

import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import {
  matchSlugTargets,
  STATSIG_EXPERIMENT_TARGETS_CONFIG_KEYS,
} from '@src/experiments/statsig-integration/experiment-targets.types'
import type { SlugExperimentTarget } from '@src/experiments/statsig-integration/experiment-targets.types'
import { useTargetedStatsigExperiment } from '@src/experiments/statsig-integration/use-targeted-statsig-experiment'
import { BreadcrumbSchema } from '@src/quince-components/src/atoms/breadCrumb'
import { getDirectory } from '@src/quince-components/src/atoms/breadCrumb/breadcrumb.helper'
import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, { getColorFromSelectedOptions } from '@src/quince-site-common/src/helpers/productHelper'

import { renderProductImages } from '../product-images/product-images-legacy'
import { getImagesSortedByColor } from '../productImages.helper'
import { getFilteredTagsForBundle } from '../utils'

export interface BundleProductImagesProps {
  bundle: Bundle
  product?: Product
  selectedOptions: ProductVariantOption[]
}

const BundleProductImages = (props: BundleProductImagesProps) => {
  const { experimentBucket: imageExperimentBucket } = useTargetedStatsigExperiment(
    STATSIG_EXPERIMENTS.PDP__PRODUCT_IMAGES_IMAGES_EXPERIMENTATION__ALL_US_CA,
    STATSIG_EXPERIMENT_TARGETS_CONFIG_KEYS[STATSIG_EXPERIMENTS.PDP__PRODUCT_IMAGES_IMAGES_EXPERIMENTATION__ALL_US_CA],
    (targets: SlugExperimentTarget[]) => matchSlugTargets(targets, props.product?.slug),
  )
  const selectedColor = getColorFromSelectedOptions(props.selectedOptions)
  const experimentColorImages = useMemo(() => {
    if (
      !props.product ||
      (imageExperimentBucket !== STATSIG_EXPERIMENTS_BUCKETS.GROUP_A &&
        imageExperimentBucket !== STATSIG_EXPERIMENTS_BUCKETS.GROUP_B)
    ) {
      return undefined
    }

    return productHelper.getImageExperimentForOptions(props.product, props.selectedOptions, {
      bucket: imageExperimentBucket,
      name: STATSIG_EXPERIMENTS.PDP__PRODUCT_IMAGES_IMAGES_EXPERIMENTATION__ALL_US_CA,
    })
  }, [props.product, props.selectedOptions, imageExperimentBucket])

  const sortedProductMedia = getImagesSortedByColor(props.bundle.images, selectedColor, experimentColorImages)
  const sortedFilteredProductMedia = sortedProductMedia.slice(0, 10)
  const directory = getDirectory(props.bundle.slug, props.bundle.title, props.bundle.hierarchy ?? undefined)
  const filteredProductTags = getFilteredTagsForBundle(props.bundle)
  return (
    <>
      <BreadcrumbSchema directory={directory} />
      {renderProductImages({
        productSlug: props.bundle.slug,
        productMedia: sortedFilteredProductMedia,
        productTitle: props.bundle.title,
        productHierarchy: props.bundle.hierarchy ?? undefined,
        hasSelectedOptionImages: true,
        hideWishList: true,
        directory: directory,
        productTags: filteredProductTags,
        isBundle: true,
      })}
    </>
  )
}

export default BundleProductImages
